import { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputLabel, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { NewOnboardingStep } from "../../types/onboarding";
import { PLAIN_BUTTONS } from "./buttons";

interface Props {
  saving: boolean;
  onClose: () => void;
  onAdd: (step: NewOnboardingStep) => Promise<string | null>;
}

const WAITS_ON: Array<{ value: string; label: string; step: Omit<NewOnboardingStep, "title"> }> = [
  { value: "client", label: "Client", step: { party: "client" } },
  { value: "chargebackhelp", label: "Ethoca", step: { party: "network", network: "chargebackhelp" } },
  { value: "verifi", label: "Verifi", step: { party: "network", network: "verifi" } },
  { value: "us", label: "Us", step: { party: "us" } },
];

// Mounted only while open, so each opening starts empty.
export default function AddStepDialog({ saving, onClose, onAdd }: Props) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [waitsOn, setWaitsOn] = useState("client");
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    const choice = WAITS_ON.find((w) => w.value === waitsOn)!;
    setError(await onAdd({ title: title.trim(), ...(text.trim() ? { text: text.trim() } : {}), ...choice.step }));
  };

  return (
    <Dialog open fullWidth maxWidth="sm" sx={PLAIN_BUTTONS} onClose={(_, reason) => { if (reason !== "backdropClick") onClose(); }}>
      <DialogTitle sx={{ pb: 0.5 }}>Add a step</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <span className="text-sm text-gray-500">For anything the checklist doesn't cover. It goes in Other steps at the end.</span>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Title" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth autoFocus
            placeholder="e.g. Verifi case 11907: chase BIN/CAID confirmation"
          />
          <div className="flex flex-col">
            <InputLabel>Waits on</InputLabel>
            <ToggleButtonGroup exclusive size="small" aria-label="Waits on" value={waitsOn} onChange={(_, value: string | null) => value && setWaitsOn(value)}>
              {WAITS_ON.map((w) => <ToggleButton key={w.value} value={w.value} sx={{ flex: 1 }}>{w.label}</ToggleButton>)}
            </ToggleButtonGroup>
          </div>
          <TextField
            label="What needs to happen (optional)" value={text} onChange={(e) => setText(e.target.value)} multiline minRows={2} size="small" fullWidth
            placeholder="What to send, ask or check, in words you can paste"
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !title.trim()} onClick={add}>Add step</Button>
      </DialogActions>
    </Dialog>
  );
}
